import { createTheme, type PaletteOptions, type Theme } from '@mui/material'
import { ICON_KNOCKOUT_COLOR_PROPERTY } from '@omnea-app/icons/styles'
import { components } from './components'
import { snackbarStyles } from './snackbar'
import { borders } from './tokens/borders'
import { gradients } from './tokens/gradients'
import { legacyPalette } from './tokens/legacyPalette'
import { darkPalette, lightPalette } from './tokens/palette'
import { radii } from './tokens/radii'
import { darkShadows, lightShadows } from './tokens/shadows'
import { spacing } from './tokens/spacing'
import { strokeWidth } from './tokens/strokeWidth'
import { typography } from './tokens/typography'

export const theme = createTheme({
  spacing,
  typography,
  // MUI replaces `values` wholesale rather than merging, so its own five have to be restated
  // alongside xxl.
  breakpoints: { values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536, xxl: 1920 } },
  shape: { borderRadius: 4, ...radii },
  palette: {
    // For backwards compatibility
    ...legacyPalette,
    ...lightPalette,
    // Transition hack
    primary: {
      main: lightPalette.bg.brand.default,
      dark: lightPalette.bg.brand.hover,
      light: lightPalette.bg.brand.muted,
      [100]: lightPalette.bg.brand.mutedHover,
      contrastText: lightPalette.text.brand.onBrand,
    },
    text: {
      ...lightPalette.text,
      // Legacy aliases for the token values
      primary: lightPalette.text.default,
      secondary: lightPalette.text.muted,
    },
    warning: {
      ...legacyPalette.warning,
      main: lightPalette.text.warning.default,
    },
    background: {
      // MUI components we haven't themed directly (ListSubheader, Slider, Autocomplete, ...)
      // fall back to this internally — keep it in sync with MuiPaper's own background.
      default: lightPalette.bg.surface.subtle,
      paper: lightPalette.bg.surface.elevated,
    },
    // Divider, ListItem and Table read `palette.divider` internally, so it cannot be dropped —
    // it is aliased onto the token instead. Application code must use `border.subtle` directly.
    divider: lightPalette.border.subtle,
    mode: 'light',
  } as PaletteOptions,
  customShadows: lightShadows,
  strokeWidth,
  borders,
  gradients,
  components,
})

export const darkTheme = createTheme(theme, {
  palette: {
    // For backwards compatibility
    ...legacyPalette,
    ...darkPalette,
    primary: {
      main: darkPalette.bg.brand.default,
      dark: darkPalette.bg.brand.hover,
      light: darkPalette.bg.brand.muted,
      [100]: darkPalette.bg.brand.mutedHover,
      contrastText: darkPalette.text.brand.onBrand,
    },
    text: {
      ...darkPalette.text,
      // Legacy aliases for the token values
      primary: darkPalette.text.default,
      secondary: darkPalette.text.muted,
    },
    warning: {
      ...legacyPalette.warning,
      main: darkPalette.text.warning.default,
    },
    background: {
      default: darkPalette.bg.surface.subtle,
      paper: darkPalette.bg.surface.elevated,
    },
    divider: darkPalette.border.subtle,
    mode: 'dark',
  } as PaletteOptions,
  customShadows: darkShadows,
})

export const globalStyles = (theme: Theme) => ({
  body: {
    // Important to override globals.css - remove that later
    backgroundColor: `${theme.palette.bg.surface.subtle} !important`,
    color: theme.palette.text.default,
    [ICON_KNOCKOUT_COLOR_PROPERTY]: theme.palette.fg.inverted,
    // Important to override globals.css - remove that later
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
  main: {
    // Important to override globals.css - remove that later
    backgroundColor: `${theme.palette.bg.surface.subtle} !important`,
  },
  html: {
    // Important to override globals.css - remove that later
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
  // Only required while we have globals.css
  button: {
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
  ...snackbarStyles(theme),
})
